import { useEffect, useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { useAuth } from '~/auth';
import { UserType } from '~/interfaces/user';
import Button from '~/components/common/button';
import Axios from '~/utils/axios';
import Loading from 'components/common/loaders/loader';
import { useForm } from 'react-hook-form';
import Input, { PhonePicker } from 'components/common/input';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import { isJarir } from '~/utils/theme';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
   offlineExperienceMobileSubmitBtnEvent,
   offlineExperienceMobileSubmitBtnFailedEvent,
   offlineExperienceMobileSubmitBtnSuccessEvent,
} from '~/utils/mixpanel-events/experience-details';
import { sentryCaptureException } from '~/errors/sentry';

type SubmitObject = {
   country_code: string;
   mobile: string;
   venue_id: string;
};

interface OfflineFormProps {
   venue_id: string;
   name?: string;
   category?: any;
   city?: string;
   organizer?: string;
   closeModal: () => void;
}

const OfflineForm = ({
   venue_id,
   closeModal,
   name,
   category,
   city,
   organizer,
}: OfflineFormProps) => {
   const { translate, lang } = useTranslate();
   const { user, authenticated } = useAuth<UserType>();
   const [loading, setLoading] = useState(false);
   const [mobileError, setMobileError] = useState('');
   const [notificationEnabled, setNotificationEnabled] = useState(true);
   const { executeRecaptcha } = useGoogleReCaptcha();
   const { register, handleSubmit, getValues, errors, setValue } = useForm<SubmitObject>({
      mode: 'onChange',
      defaultValues: {
         country_code: '966',
      },
   });
   const values = getValues();

   const submitHandler = async ({ country_code, mobile }: SubmitObject) => {
      if (loading) return;
      const trackObj = {
         mobile: mobile,
         experienceId: venue_id,
         experienceCity: city,
         experienceCategory: category?.title || category,
         experienceName: name,
         providerName: organizer,
      };
      offlineExperienceMobileSubmitBtnEvent(trackObj);
      setLoading(true);
      try {
         const captcha = await executeRecaptcha?.();
         const { data } = await Axios.post('/v2/booking_interest', {
            country_code: country_code || 'null',
            mobile: user ? user.cell_number : mobile,
            venue_id,
            is_user: authenticated,
            user_id: user?.user_id,
            captcha,
         });

         if (data.status == 'Success' || data.original?.status === 'Success') {
            offlineExperienceMobileSubmitBtnSuccessEvent({
               ...trackObj,
               mobile: user ? user.cell_number : mobile,
            });
            setLoading(false);
            toast.success(translate({ id: 'booking:offline register success' }));
            closeModal();
         } else {
            if (data.error) {
               setMobileError(data.error.cell_number);
            }
            throw new Error(data?.error?.cell_number || data.original.error.description);
         }
      } catch (error: any) {
         setLoading(false);
         offlineExperienceMobileSubmitBtnFailedEvent({
            error: error.message,
            mobile: user ? user.cell_number : mobile,
            experienceId: venue_id,
            experienceCity: city,
            experienceCategory: category,
            experienceName: name,
            providerName: organizer,
         });
         sentryCaptureException(error, { extra: trackObj });
      }
   };

   useEffect(() => {
      register('country_code', {
         required: translate({ id: 'auth:required field' }),
      });
   }, [register, translate]);

   return (
      <div>
         <div className="pb-2 text-2xl font-semibold">
            <Translate id="booking:experience is offline" />
         </div>
         <div className="pb-4 text-sm">
            <Translate parseHTML id="booking:offline experience description" />
         </div>
         <form onSubmit={handleSubmit(submitHandler)}>
            {!user ? (
               <div className="w-full mb-6">
                  <Input
                     type="text"
                     label={translate({ id: 'jarirauth:enter number' })}
                     className="bg-gray-100 rounded-default"
                     groupClassName=""
                     placeholder="XXXXXXXXX"
                     id="mobile"
                     name="mobile"
                     inputMode="numeric"
                     autoCorrect="off"
                     autoComplete="none"
                     autoSave="off"
                     inputWrapperClassName="relative"
                     innerRef={register({
                        required: translate({ id: 'auth:required field' }),
                     })}
                     error={mobileError}
                     valid={!!values.mobile}
                     prependClassName={classNames(
                        !!errors.country_code && 'border-primary-error',
                        !!values.country_code && 'border-primary-success'
                     )}
                     prepend={
                        <div className="w-24 h-full">
                           <PhonePicker
                              placeholder="XXX"
                              onCountrySelected={({ phone }) =>
                                 setValue('country_code', phone, {
                                    shouldValidate: true,
                                 })
                              }
                           />
                        </div>
                     }
                  />
               </div>
            ) : null}

            <div>
               <Input
                  type="checkbox"
                  className="items-center mb-4 focus:outline-none"
                  name="notification_status"
                  label={translate({ id: 'booking:offline experience alert me label' })}
                  defaultChecked={notificationEnabled}
                  onChange={() => setNotificationEnabled(!notificationEnabled)}
               />
            </div>

            <Button
               btnType="submit"
               id="mobile-login"
               className={classNames(
                  'w-full mt-5 border-0',
                  isJarir ? 'bg-jarir-primary' : 'bg-primary-green'
               )}
               disabled={(user ? false : loading || !values.mobile) || !notificationEnabled}>
               {loading ? <Loading color="white" /> : <Translate id="feedback:submit" />}
            </Button>

            <div className="text-xs text-gray-400 text-center pt-3">
               <Translate id="auth:on submit you agrees to our" />{' '}
               <a
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-500 underline"
                  href={
                     isJarir
                        ? `https://tickets.jarir.com/${lang}/terms-conditions`
                        : 'https://halayalla.com/terms.php'
                  }>
                  <Translate id="auth:terms and conditions" />
               </a>
            </div>
         </form>
      </div>
   );
};

export default OfflineForm;
